import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Package, Send } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useImageUrls } from '@/hooks/useImageUrls';
import { trackActivity } from '@/lib/activityTracking';
import { composeRoute, shareDraft } from '@/lib/entityLinks';
import { Button } from '@/components/ui/button';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META = {
  id: 'chat-listing-card',
  label: 'Listing card in chat',
  description: 'Shows a listing with its photo and key details inside a Your Next Move conversation, and prepares it to send to a buyer.',
  triggerType: 'panel' as const,
  personas: ['seller', 'buyer_seller'] as ('seller' | 'buyer_seller')[],
};

interface ChatListing {
  id: string; strain_name: string | null; product_form: string | null; thc_percent: number | null;
  quantity_value: number | null; quantity_unit: string | null; price_value: number | null; price_unit: string | null;
  currency: string | null; images: string[] | null;
}

/** Read through the user's own session, so the card shows only what they may already see. */
function useChatListing(id: string) {
  return useQuery({
    queryKey: ['chat-listing', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('listings')
        .select('id,strain_name,product_form,thc_percent,quantity_value,quantity_unit,price_value,price_unit,currency,images')
        .eq('id', id).maybeSingle();
      if (error) throw error;
      return data as ChatListing | null;
    },
    staleTime: 60_000,
  });
}

/** Listing card inside an answer: photo, strain, type, THC, quantity, price. */
export function ListingArtifactCard({ id }: { id: string }) {
  useFeatureRegistration(FEATURE_META);
  const q = useChatListing(id);
  const first = q.data?.images?.[0];
  const { getUrl } = useImageUrls(first ? [first] : []);
  if (q.isLoading) return <span className="my-2 flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" />Loading listing…</span>;
  const l = q.data;
  if (!l) return <span className="my-2 block text-xs text-muted-foreground">This listing isn't available to you.</span>;
  const facts = [
    l.product_form,
    l.thc_percent != null ? <><span className="num">{l.thc_percent}%</span> THC</> : null,
    l.quantity_value != null ? <><span className="num">{l.quantity_value}</span>{l.quantity_unit ? ` ${l.quantity_unit}` : ''}</> : null,
    l.price_value != null ? <><span className="num">{`${l.currency === 'USD' || !l.currency ? '$' : `${l.currency} `}${l.price_value}`}</span> per {l.price_unit ?? 'unit'}</> : null,
  ].filter(Boolean);
  return (
    <Link to={`/listings/${l.id}`} onClick={() => trackActivity('ask_entity_opened', { kind: 'listing_card' })}
      className="my-2 flex max-w-md items-center gap-3 rounded-lg border bg-card p-2 text-foreground no-underline hover:bg-muted/50">
      {first ? (
        <img src={getUrl(first)} alt={l.strain_name ?? 'Listing photo'} className="h-20 w-20 shrink-0 rounded-md object-cover" loading="lazy" />
      ) : (
        <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-md bg-muted"><Package className="h-6 w-6 text-muted-foreground" aria-hidden /></span>
      )}
      <span className="min-w-0">
        <span className="block truncate font-semibold">{l.strain_name ?? 'Untitled listing'}</span>
        <span className="block text-xs text-muted-foreground">{facts.length ? facts.map((f, i) => <span key={i}>{i > 0 && ' · '}{f}</span>) : 'Details not filled in yet'}</span>
      </span>
    </Link>
  );
}

/**
 * Staged send: creates the same tracked share link the Share dialog makes, then opens the
 * Messages composer to that company with the link in a draft. The user reviews and presses Send.
 */
export function ShareListingAction({ listingId, companyId, children }: { listingId: string; companyId: string; children: ReactNode }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const q = useChatListing(listingId);
  const go = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('create_share_link', { p_listing_ids: [listingId], p_share_visibility: {} } as never);
      if (error) throw error;
      const slug = ((Array.isArray(data) ? data[0] : data) as { slug?: string } | null)?.slug;
      if (!slug) throw new Error('no slug');
      let url = `${window.location.origin}/s/${slug}`;
      try {
        const { data: s } = await supabase.functions.invoke('shorten-url', { body: { url } });
        if ((s as { shortUrl?: string } | null)?.shortUrl) url = (s as { shortUrl: string }).shortUrl;
      } catch { /* long link still works */ }
      trackActivity('ask_entity_opened', { kind: 'share_listing' });
      navigate(composeRoute(companyId, shareDraft(q.data?.strain_name ?? 'this listing', url)));
    } catch {
      toast.error("Couldn't prepare that share", { description: 'You can only share your own listings. Please try again.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button type="button" size="sm" className="my-1 h-8 gap-1.5" disabled={busy} onClick={go}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5" aria-hidden />}{children}
    </Button>
  );
}
